<template>
  <div id="app">
    <Header></Header>
    <router-view/>
    <Footer></Footer>
    <GoTop></GoTop>
  </div>
</template>

<script>
import seoConfig from '@/config/seo'

export default {
  name: 'App',
  metaInfo() {
    // 統一 canonical 指向主域名，解決「重複網頁」「Google 選擇與用戶指定不同」等問題
    const canonicalBase = seoConfig.domain
    let path = this.$route.path
    if (path.toLowerCase() === '/index.html') path = '/'
    if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1)
    // newsdetail 的 ?id= 必須包含在 canonical 中，否則每篇新聞會被視為重複頁
    let canonicalUrl = canonicalBase + (path === '/' ? '' : path)
    if (path === '/newsdetail' && this.$route.query && this.$route.query.id) {
      canonicalUrl = `${canonicalBase}/newsdetail?id=${encodeURIComponent(this.$route.query.id)}`
    }

    return {
      htmlAttrs: {
        lang: this.$i18n.locale === 'zh-TW' ? 'zh-Hant' : 'en'
      },
      title: '新領海機械行有限公司 - 專業機械設備供應商',
      titleTemplate: '%s | 新領海機械行有限公司',
      meta: [
        // 基础 SEO 标签
        { name: 'description', content: '新領海機械行有限公司是香港領先的機械設備供應商，提供專業的機械租賃、維修和銷售服務。24小時全天候服務，15分鐘響應，1小時到達現場。' },
        { name: 'keywords', content: '新領海機械行,香港機械公司,機械租賃,機械維修,機械銷售,香港國際機場,啟德新區,填海工程,徐工集團香港代理,工程機械' },
        { name: 'author', content: '新領海機械行有限公司' },
        { name: 'robots', content: 'index, follow' },

        // Open Graph 标签 - 使用当前域名
        { property: 'og:title', content: '新領海機械行有限公司 - 香港專業工程機械服務供應商 | 徐工集團香港代理' },
        { property: 'og:description', content: '香港領先的工程機械供應商，徐工集團香港一級代理商。機械租賃、維修、銷售一站式服務，15分鐘響應，1小時到場。' },
        { property: 'og:type', content: 'website' },
        // og:url 與 canonical 一致，指向主域名
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:image', content: canonicalBase + '/static/img/logo_black.png' },
        { property: 'og:site_name', content: '新領海機械行有限公司' },
        { property: 'og:locale', content: this.$i18n.locale === 'zh-TW' ? 'zh_HK' : 'en_US' },

        // Twitter Card 标签
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@newoceanmachines' },
        { name: 'twitter:title', content: '新領海機械行有限公司 - 香港專業工程機械服務供應商 | 徐工集團香港代理' },
        { name: 'twitter:description', content: '香港領先的工程機械供應商，徐工集團香港一級代理商。機械租賃、維修、銷售一站式服務，15分鐘響應，1小時到場。' },
        { name: 'twitter:image', content: canonicalBase + '/static/img/logo_black.png' }
      ],
      link: [
        // 动态 canonical：当前域名+规范化路径，解决重复网页问题
        { rel: 'canonical', href: canonicalUrl, vmid: 'canonical' }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            'name': '新領海機械行有限公司',
            'alternateName': ['New Ocean Mechanical', '新領海機械'],
            'url': seoConfig.domain,
            'logo': seoConfig.domain + '/static/img/logo_black.png',
            'description': '香港領先的工程機械設備供應商，徐工集團香港一級代理商。提供機械租賃、維修保養、配件銷售，15分鐘響應，1小時到達現場，24小時全天候服務。',
            'foundingDate': '2015',
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': '香港新界屯門建發里4號',
              'addressLocality': '天水圍',
              'addressRegion': '新界',
              'postalCode': '999077',
              'addressCountry': 'HK'
            },
            'contactPoint': [
              {
                '@type': 'ContactPoint',
                'telephone': '+852-3956-3953',
                'contactType': 'customer service',
                'areaServed': 'HK',
                'availableLanguage': ['zh-HK', 'en'],
                'hoursAvailable': { '@type': 'OpeningHoursSpecification', 'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], 'opens': '00:00', 'closes': '23:59' }
              },
              {
                '@type': 'ContactPoint',
                'telephone': '+852-6709-6716',
                'contactType': 'technical support',
                'areaServed': 'HK',
                'availableLanguage': ['zh-HK', 'en']
              }
            ],
            'sameAs': [
              'https://www.facebook.com/newoceanmachines',
              'https://twitter.com/newoceanmachines',
              'https://www.linkedin.com/company/newoceanmachines'
            ]
          }
        },
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': '新領海機械行有限公司',
            'url': seoConfig.domain,
            'description': '香港專業工程機械服務供應商，徐工集團香港一級代理商。機械租賃、維修、銷售一站式服務。',
            'inLanguage': ['zh-HK', 'en'],
            'publisher': { '@type': 'Organization', 'name': '新領海機械行有限公司' },
            'potentialAction': {
              '@type': 'SearchAction',
              'target': { '@type': 'EntryPoint', 'urlTemplate': seoConfig.domain + '/newsinformation?search={search_term_string}' },
              'query-input': 'required name=search_term_string'
            }
          }
        }
      ]
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/App.vue